/**
 * Catalogo licenze (SuperAdmin MAE).
 *
 * v1.8.0: una `license` (catalogo, 103 voci) è effettivamente attivabile solo se
 * esiste un `fwk-addon` con stesso `code` e `isAvailable=true` (52 oggi).
 * Mostriamo tutte e marciamo "Coming soon" le voci a roadmap commerciale.
 */
import { createFileRoute } from '@tanstack/react-router';
import { ActionIcon, Center, Group, Loader, ScrollArea, SimpleGrid, Table, Text, TextInput, rem } from '@mantine/core';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { IconApps, IconCircleCheck, IconClockPause, IconCrown, IconLicense, IconSearch, IconX } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type CSSProperties } from 'react';
import { fetchAllPages } from '@/shared/api/jsonapi/client';
import { EmptyState } from '@/shared/components/EmptyState';
import { StatCard } from '@/shared/components/StatCard';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { mw, MW_CARD_SHADOW, MW_CODE_STYLE } from '@/shared/mobile/tokens';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { MobileLicensesCatalog } from '@/features/admin/mobile/MobileLicensesCatalog';
import type { FwkAddon, License } from '@/shared/types/api.gen';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { Paginated } from '@/shared/components/DataTable/TablePagination';

export const Route = createFileRoute('/_app/admin/licenses-catalog')({ component: LicensesCatalogRoute });

/** Selettore: catalogo licenze mobile (MaeDesign) vs tabella desktop. */
function LicensesCatalogRoute() {
  const isMobile = useIsMobileShell();
  return isMobile ? <MobileLicensesCatalog /> : <LicensesCatalogPage />;
}

type LicenseRow = License & Denormalized & { id: string };
type AddonRow = FwkAddon & Denormalized & { id: string };

type StatusKey = 'all' | 'available' | 'unavailable';

function LicensesCatalogPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusKey>('all');

  const licensesQ = useQuery({
    queryKey: ['licenses-all'],
    staleTime: 5 * 60_000,
    queryFn: () => fetchAllPages<LicenseRow>('licenses', { sort: ['category', 'code'] }, 200),
  });

  const addonsQ = useQuery({
    queryKey: ['fwk-addons-all'],
    staleTime: 5 * 60_000,
    queryFn: () => fetchAllPages<AddonRow>('fwk-addons', {}, 200),
  });

  const addonByCode = useMemo(() => {
    const m = new Map<string, AddonRow>();
    for (const a of addonsQ.data ?? []) {
      if (a.code) m.set(a.code, a);
    }
    return m;
  }, [addonsQ.data]);

  const rowState = useMemo(
    () => (l: LicenseRow): { kind: 'available' | 'roadmap' | 'no-addon'; addon?: AddonRow } => {
      const addon = l.code ? addonByCode.get(l.code) : undefined;
      if (!addon) return { kind: 'no-addon' };
      return { kind: addon.isAvailable ? 'available' : 'roadmap', addon };
    },
    [addonByCode],
  );

  const filtered = useMemo(() => {
    const rows = licensesQ.data ?? [];
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (term && !(
        (r.code ?? '').toLowerCase().includes(term)
        || (r.name ?? '').toLowerCase().includes(term)
        || (r.category ?? '').toLowerCase().includes(term)
      )) return false;
      if (status === 'available' && rowState(r).kind !== 'available') return false;
      if (status === 'unavailable' && rowState(r).kind === 'available') return false;
      return true;
    });
  }, [licensesQ.data, search, status, rowState]);

  const totals = useMemo(() => {
    const rows = licensesQ.data ?? [];
    const available = rows.filter((l) => rowState(l).kind === 'available').length;
    const categories = new Set(rows.map((l) => l.category).filter(Boolean)).size;
    return { total: rows.length, available, unavailable: rows.length - available, categories };
  }, [licensesQ.data, rowState]);

  const loading = licensesQ.isLoading || addonsQ.isLoading;
  const failed = [licensesQ, addonsQ].find((q) => q.isError);

  const card: CSSProperties = {
    border: `1px solid ${mw.line}`, borderRadius: 16, background: mw.surface, boxShadow: MW_CARD_SHADOW,
  };
  const thStyle: CSSProperties = {
    background: mw.surface2, color: mw.ink300, fontSize: 'var(--fs-compact)', fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: `1px solid ${mw.line}`, whiteSpace: 'nowrap',
  };
  const chip = (label: string, bg: string, color: string, codice = false) => (
    <span style={{ display: 'inline-block', fontSize: codice ? 'var(--fs-compact)' : 'var(--fs-compact)', fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: bg, color, ...(codice ? MW_CODE_STYLE : null), whiteSpace: 'nowrap' }}>{label}</span>
  );

  const FILTERS: { key: StatusKey; label: string; count: number }[] = [
    { key: 'all', label: 'Tutte', count: totals.total },
    { key: 'available', label: 'Attivabili', count: totals.available },
    { key: 'unavailable', label: 'Non attivabili', count: totals.unavailable },
  ];

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconCrown size={22} />}
        title="Catalogo licenze"
        sub="Inventario completo delle licenze del framework e loro attivabilità."
        tone="bordeaux"
      />

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing={14} mb={18}>
        <StatCard label="Licenze totali" value={totals.total} icon={IconLicense} color="brand" loading={loading} />
        <StatCard label="Attivabili" value={totals.available} icon={IconCircleCheck} color="green" loading={loading} />
        <StatCard label="Non attivabili" value={totals.unavailable} icon={IconClockPause} color="orange" loading={loading} />
        <StatCard label="Categorie" value={totals.categories} icon={IconApps} color="blue" loading={loading} />
      </SimpleGrid>

      {failed && (
        <div style={{ marginBottom: 16 }}>
          <QueryErrorAlert error={failed.error} title="Impossibile leggere il catalogo licenze" onRetry={() => { void licensesQ.refetch(); void addonsQ.refetch(); }} />
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', border: `1px solid ${mw.line}`, background: mw.accentTint, borderRadius: 14, padding: '12px 16px', marginBottom: 16 }}>
        <IconLicense size={18} style={{ color: mw.accentText, flexShrink: 0, marginTop: 1 }} />
        <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, lineHeight: 1.5 }}>
          Una voce è attivabile solo se esiste un <code>fwk-addon</code> con lo stesso codice e <code>isAvailable=true</code>. Le voci <strong>Coming soon</strong> sono a roadmap: il sistema rifiuta di attivarle.
        </Text>
      </div>

      <div style={{ ...card, padding: '14px 16px', marginBottom: 16 }}>
        <Group wrap="wrap" gap="sm">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {FILTERS.map((f) => {
              const active = f.key === status;
              return (
                <button
                  key={f.key}
                  type="button"
                  className="mw-pressable"
                  onClick={() => setStatus(f.key)}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, height: rem(34), padding: '0 14px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-compact)', fontWeight: 600, whiteSpace: 'nowrap', border: active ? `1px solid ${mw.brand}` : `1px solid ${mw.line}`, background: active ? mw.brandFill : mw.surface, color: active ? 'var(--on-brand)' : mw.ink500 }}
                >
                  {f.label}
                  <span style={{ fontSize: 'var(--fs-compact)', fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: active ? 'rgba(255,255,255,0.22)' : mw.surface2, color: active ? '#fff' : mw.ink300 }}>{f.count}</span>
                </button>
              );
            })}
          </div>
          <TextInput
            placeholder="Cerca per codice, nome o categoria…"
            leftSection={<IconSearch size={14} />}
            rightSection={search ? (
              <ActionIcon variant="subtle" size="sm" aria-label="Cancella ricerca" onClick={() => setSearch('')}>
                <IconX size={12} />
              </ActionIcon>
            ) : null}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ marginLeft: 'auto', minWidth: 280, flex: 1 }}
          />
        </Group>
      </div>

      <div style={{ ...card, overflow: 'hidden' }}>
        {loading ? (
          <Center py={60}><Loader /></Center>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={IconLicense}
            title="Nessun risultato"
            description="Il catalogo contiene licenze, ma nessuna corrisponde alla ricerca e al filtro di stato attivi."
            actionLabel="Azzera ricerca e filtro"
            onAction={() => { setSearch(''); setStatus('all'); }}
            py="lg"
          />
        ) : (
          <Paginated rows={filtered} label="licenze">
          {(pageRows) => (
          <ScrollArea type="auto">
            <Table verticalSpacing={12} horizontalSpacing="md" highlightOnHover stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ ...thStyle, minWidth: 200 }}>Codice</Table.Th>
                  <Table.Th style={thStyle}>Nome</Table.Th>
                  <Table.Th style={{ ...thStyle, minWidth: 150 }}>Categoria</Table.Th>
                  <Table.Th style={{ ...thStyle, minWidth: 90 }}>Livello</Table.Th>
                  <Table.Th style={{ ...thStyle, minWidth: 170 }}>Stato</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pageRows.map((l) => {
                  const st = rowState(l);
                  return (
                    <Table.Tr key={l.id}>
                      <Table.Td>{chip(l.code ?? '—', mw.surface2, mw.ink900, true)}</Table.Td>
                      <Table.Td>
                        <Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 600, color: mw.ink900 }}>{l.name ?? '—'}</Text>
                        {l.description && <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }} lineClamp={2}>{l.description}</Text>}
                      </Table.Td>
                      <Table.Td>{l.category ? chip(l.category, mw.accentTint, mw.accentText) : <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink300 }}>—</Text>}</Table.Td>
                      <Table.Td>{l.level ? chip(String(l.level), mw.brandTint, mw.brandText) : <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink300 }}>—</Text>}</Table.Td>
                      <Table.Td>
                        {st.kind === 'available' && chip(`● Disponibile${st.addon?.introducedInVersion ? ` · ${st.addon.introducedInVersion}` : ''}`, mw.successBg, mw.success)}
                        {st.kind === 'roadmap' && chip('● Coming soon', mw.warningBg, mw.warning)}
                        {st.kind === 'no-addon' && chip('Nessun addon', mw.surface2, mw.ink500)}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          )}
          </Paginated>
        )}
      </div>
    </MaeShell>
  );
}
